"use client";

import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { ArrowUpRight, CheckCircle2, Heart, Sparkles } from "lucide-react";
import {
  LIVE_TESTIMONIALS_FALLBACK,
  rankLiveTestimonials,
  type LiveTestimonial,
  type LiveTestimonialsFeed,
} from "@/lib/live-testimonials";

const ROTATION_MS = 6_000;

function shortDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Recent"
    : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(date);
}

function Avatar({ testimonial }: { testimonial: LiveTestimonial }) {
  return testimonial.avatarUrl ? (
    <img
      src={testimonial.avatarUrl}
      alt=""
      width={40}
      height={40}
      loading="lazy"
      className="size-10 rounded-full border border-border bg-muted object-cover"
    />
  ) : (
    <span className="grid size-10 place-items-center rounded-full border border-border bg-muted data-num text-xs font-bold text-foreground">
      {testimonial.authorName.slice(0, 1)}
    </span>
  );
}

function XMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="size-3.5 fill-current">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}

function QueueCard({ testimonial, active }: { testimonial: LiveTestimonial; active: boolean }) {
  return (
    <div
      className={`grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-xl border px-3 py-3 transition-colors ${
        active
          ? "border-success/30 bg-success/10"
          : "border-border bg-muted/30"
      }`}
    >
      <span className="relative">
        <Avatar testimonial={testimonial} />
        {active ? (
          <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-card bg-success" />
        ) : null}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
          <span className="truncate">{testimonial.authorName}</span>
          <CheckCircle2 className="size-3.5 shrink-0 text-success" />
        </span>
        <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
          {testimonial.highlight || "New Upside win"} · {shortDate(testimonial.createdAt)}
        </span>
      </span>
      <a
        href={testimonial.tweetUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={`Verify @${testimonial.authorUsername}'s win on X`}
        className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        <ArrowUpRight className="size-4" />
      </a>
    </div>
  );
}

export function LiveWinsSection({ presentation = "rotating", onShowMore, renderCard }: { presentation?: "rotating" | "cards"; onShowMore?: () => void; renderCard?: (testimonial: LiveTestimonial) => ReactNode } = {}) {
  const [feed, setFeed] = useState<LiveTestimonialsFeed>(() => ({
    ...LIVE_TESTIMONIALS_FALLBACK,
    testimonials: rankLiveTestimonials(LIVE_TESTIMONIALS_FALLBACK.testimonials),
  }));
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const testimonials = feed.testimonials;

  useEffect(() => {
    let cancelled = false;
    let lastFetchAt = 0;
    let timer: number | undefined;
    const refresh = async () => {
      lastFetchAt = Date.now();
      try {
        const response = await fetch("/api/testimonials/live", {
          cache: "no-store",
        });
        if (!response.ok) return;
        const next = (await response.json()) as LiveTestimonialsFeed;
        if (!cancelled && next.testimonials?.length) {
          setFeed({ ...next, testimonials: rankLiveTestimonials(next.testimonials) });
        }
      } catch {
      }
    };
    const scheduleNext = () => {
      timer = window.setTimeout(onTimer, Math.max(0, 5 * 60_000 - (Date.now() - lastFetchAt)));
    };
    const onTimer = () => {
      if (document.hidden) return;
      void refresh();
      scheduleNext();
    };
    const syncRefresh = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      if (document.hidden) return;
      if (Date.now() - lastFetchAt >= 5 * 60_000) void refresh();
      scheduleNext();
    };
    syncRefresh();
    document.addEventListener("visibilitychange", syncRefresh);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", syncRefresh);
    };
  }, []);

  useEffect(() => {
    if (presentation === "cards" || testimonials.length < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const timer = window.setInterval(
      () => setActiveIndex((index) => (index + 1) % testimonials.length),
      ROTATION_MS,
    );
    return () => window.clearInterval(timer);
  }, [testimonials.length, presentation]);

  const featured = testimonials[activeIndex % testimonials.length];
  const queue = useMemo(
    () => [1, 2, 3].map((offset) => testimonials[(activeIndex + offset) % testimonials.length]),
    [activeIndex, testimonials],
  );
  if (!featured) return null;

  if (presentation === "cards") return (
    <section id="live-wins" className="bg-background py-10 sm:py-14">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Recent reviews. Original receipts.</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{feed.source === "x" ? "Updated from @UpsideTools on X." : "Selected community posts."}</p>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {testimonials.slice(0, visibleCount).map((testimonial) => renderCard ? <Fragment key={testimonial.tweetId}>{renderCard(testimonial)}</Fragment> : (
            <article key={testimonial.tweetId} className="flex flex-col rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center gap-3"><Avatar testimonial={testimonial} /><div><p className="text-sm font-semibold">{testimonial.authorName}</p><p className="text-xs text-muted-foreground">@{testimonial.authorUsername} · {shortDate(testimonial.createdAt)}</p></div></div>
              <blockquote className="my-4 flex-1 text-sm leading-relaxed">“{testimonial.text}”</blockquote>
              {testimonial.mediaUrl && <a href={testimonial.mediaUrl} target="_blank" rel="noopener noreferrer" className="mb-3 block overflow-hidden rounded-xl border border-border bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                <img src={testimonial.mediaUrl} alt={`${testimonial.authorName}'s posted result. Tap to view full size.`} loading="lazy" decoding="async" className="h-auto max-h-[28rem] w-full object-contain" />
              </a>}
              <a href={testimonial.tweetUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Read original & see receipt <ArrowUpRight className="size-4" aria-hidden /></a>
            </article>
          ))}
        </div>
        {visibleCount < testimonials.length && <button type="button" onClick={() => { setVisibleCount((count) => count + 3); onShowMore?.(); }} className="mt-5 min-h-12 rounded-xl border border-border px-5 text-sm font-semibold transition-colors duration-150 hover:bg-muted motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Show more reviews</button>}
        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">Selected, user-reported results are not typical. Betting involves risk.</p>
      </div>
    </section>
  );

  return (
    <section id="live-wins" className="bg-background py-20">
      <div className="mx-auto max-w-7xl px-4">
        <div className="mx-auto mb-8 max-w-2xl text-center">
          <h2 className="text-balance text-2xl font-semibold tracking-[-0.035em] text-foreground md:text-4xl">
            Wins keep coming in.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Reposted by @UpsideTools. Every win links to the original post on X.
          </p>
        </div>

        <div className="grid overflow-hidden rounded-panel border border-border bg-card shadow-sm surface-toplight lg:grid-cols-[1.35fr_0.65fr]">
          <article
            key={featured.tweetId}
            className="group grid animate-fade-in-up motion-reduce:animate-none md:grid-cols-[0.95fr_1.05fr]"
          >
            <div className="relative aspect-[16/10] overflow-hidden bg-black md:aspect-auto md:min-h-full">
              {featured.mediaUrl ? (
                <img
                  src={featured.mediaUrl}
                  alt={`${featured.authorName}'s posted result`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 size-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02] motion-reduce:transition-none"
                />
              ) : (
                <div className="grid size-full place-items-center bg-muted/40">
                  <Sparkles className="size-10 text-muted-foreground" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-card/25" />
              {featured.highlight ? (
                <span className="absolute left-4 top-4 rounded-full border border-success/30 bg-zinc-950/85 px-3 py-1 text-sm font-bold text-success shadow-glow-success-subtle backdrop-blur-sm">
                  {featured.highlight}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col justify-between p-4 sm:p-6">
              <div>
                <div className="flex items-center justify-between gap-3">
                  <a
                    href={featured.tweetUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Verify @${featured.authorUsername}'s win on X`}
                    className="inline-flex items-center gap-1.5 rounded text-[11px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                  >
                    <XMark /> {shortDate(featured.createdAt)}
                  </a>
                </div>
                <blockquote className="mt-5 text-balance text-lg font-semibold leading-relaxed tracking-[-0.02em] text-foreground sm:text-xl">
                  “{featured.text}”
                </blockquote>
              </div>
              <div className="mt-5 flex items-center justify-between gap-4 border-t border-border pt-4">
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar testimonial={featured} />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                      <span className="truncate">{featured.authorName}</span>
                      <CheckCircle2 className="size-4 shrink-0 text-success" />
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      @{featured.authorUsername}
                    </span>
                  </span>
                </span>
                <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                  <Heart className="size-3.5" /> {featured.likeCount}
                </span>
              </div>
            </div>
          </article>

          <aside className="border-t border-border p-4 sm:p-5 lg:border-l lg:border-t-0">
            <div className="space-y-2.5">
              {queue.map((testimonial, index) => (
                <div
                  key={testimonial.tweetId}
                  className={index === 2 ? "hidden sm:block" : undefined}
                >
                  <QueueCard testimonial={testimonial} active={index === 0} />
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
              Results are user-reported and not typical; betting involves risk.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}
